/**
 * Rewarded ads abstraction.
 *  - NATIVE (Capacitor + AdMob): real ads, real revenue.
 *  - WEB: rewarded-ad networks are weak / sparse, so we skip the gate
 *    and credit instantly. Daily caps + idempotency still enforced by
 *    the backend, so this isn't a money leak — the user just doesn't
 *    have to watch a fake video.
 *
 * Backend is the source of truth for crediting chips/gems — never credit
 * locally.
 */
import axios from "axios";
import { API, getOrCreateUserId } from "./api";

const isCapacitorNative = () => {
  try {
    return typeof window !== "undefined"
      && window.Capacitor
      && window.Capacitor.isNativePlatform
      && window.Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export async function fetchAdQuota() {
  const user_id = getOrCreateUserId();
  const res = await axios.get(`${API}/ads/quota/${user_id}`);
  return res.data; // { used, remaining, caps }
}

export async function fetchAdRewards() {
  const res = await axios.get(`${API}/ads/rewards`);
  return res.data; // { rewards, daily_caps }
}

/**
 * Show a rewarded ad. Resolves with { chips } on completion, or rejects on failure.
 * Native path is wired via @capacitor-community/admob if installed.
 *
 * @param {string} reward_id - one of: chips_50, chips_200, chips_rescue_100, chips_double_down
 * @param {object} extra - optional fields. For chips_double_down: { stake: <number> }
 */
export async function showRewardedAd(reward_id = "chips_50", extra = {}) {
  const user_id = getOrCreateUserId();
  const transaction_id = `tx_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  if (isCapacitorNative()) {
    try {
      const { AdMob, RewardAdPluginEvents } = await import("@capacitor-community/admob");
      const adId = process.env.REACT_APP_ADMOB_REWARDED_ID || "ca-app-pub-3940256099942544/5224354917";
      await AdMob.prepareRewardVideoAd({ adId });
      const got = await new Promise((resolve, reject) => {
        const sub = AdMob.addListener(RewardAdPluginEvents.Rewarded, (rew) => {
          sub.remove?.();
          resolve(rew);
        });
        AdMob.addListener(RewardAdPluginEvents.FailedToLoad, (e) => reject(e));
        AdMob.showRewardVideoAd();
      });
      if (!got) throw new Error("Ad failed to play");
    } catch (e) {
      console.warn("AdMob failure; falling back to instant reward:", e);
    }
  } else {
    // Web — skip the fake ad gate; just tap-feedback delay so the button
    // animation reads as intentional. Daily caps + idempotency are still
    // enforced server-side so this is NOT a money leak.
    await new Promise((r) => setTimeout(r, 600));
  }
  const payload = { user_id, reward_id, transaction_id, ...extra };
  const res = await axios.post(`${API}/ads/claim`, payload);
  return res.data;
}
